import { useState } from 'react'
import { Head } from '@inertiajs/react'
import { UsersIcon } from '@heroicons/react/24/outline'
import { withAppLayout, PageHeading } from '@/components/layout'
import { Button, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui'
import { RoleBadge, PendingBadge } from '@/components/common/Badges'
import { EmptyState } from '@/components/common/EmptyState'
import { RowActions, type RowAction } from '@/components/common/RowActions'
import { InviteUserModal } from '@/components/users/InviteUserModal'
import { useTranslations } from '@/lib/i18n'

interface UserRow {
  id: number
  name: string
  email: string
  role: string
  merchants: string[]
  invitation_pending: boolean
  is_self: boolean
  can_edit: boolean
  can_delete: boolean
  can_view_activity: boolean
}

function UsersIndex({
  users,
  roles,
  assignable_merchants,
}: {
  users: UserRow[]
  roles: string[]
  assignable_merchants: { id: number; name: string }[]
}) {
  const { t } = useTranslations()
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <>
      <Head title={t('users.title')} />
      <PageHeading
        title={t('users.title')}
        subtitle={t('users.subtitle')}
        actions={
          <Button color="brand" onClick={() => setModalOpen(true)}>
            {t('users.invite')}
          </Button>
        }
      />

      <InviteUserModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        roles={roles}
        assignableMerchants={assignable_merchants}
      />

      {users.length === 0 ? (
        <EmptyState
          className="mt-8"
          icon={UsersIcon}
          title={t('users.empty')}
          action={
            <Button color="brand" onClick={() => setModalOpen(true)}>
              {t('users.invite')}
            </Button>
          }
        />
      ) : (
        <Table className="mt-8">
          <TableHead>
            <TableRow>
              <TableHeader>{t('users.name')}</TableHeader>
              <TableHeader>{t('users.role')}</TableHeader>
              <TableHeader>{t('users.assigned_merchants')}</TableHeader>
              <TableHeader className="text-right">{t('common.actions')}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => {
              const items: RowAction[] = []
              if (user.can_view_activity)
                items.push({ label: t('users.view_activity'), href: `/activity?user_id=${user.id}` })
              if (user.can_edit) items.push({ label: t('common.edit'), href: `/users/${user.id}/edit` })

              return (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="font-medium text-text-strong">{user.name}</div>
                    <div className="text-text/60">{user.email}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <RoleBadge role={user.role} label={t(`roles.${user.role}`)} />
                      {user.invitation_pending && <PendingBadge label={t('users.pending')} />}
                    </div>
                  </TableCell>
                  <TableCell className="text-text/80">
                    {user.merchants.length > 0 ? user.merchants.join(', ') : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {user.is_self && <span className="text-xs text-text/50">{t('users.you')}</span>}
                      <RowActions
                        menuLabel={t('common.actions')}
                        items={items}
                        destroy={
                          user.can_delete
                            ? {
                                url: `/users/${user.id}`,
                                label: t('common.delete'),
                                confirm: t('users.delete_confirm'),
                              }
                            : undefined
                        }
                      />
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </>
  )
}

UsersIndex.layout = withAppLayout

export default UsersIndex
